/**
 * Nosto Product Recommendations - Clean Implementation
 *
 * Flow:
 * 1. Call Nosto API to get recommended products
 * 2. Immediately render placeholder cards with Nosto data (fast)
 * 3. Fetch full product card HTML from Shopify in parallel
 * 4. Replace placeholders with full cards as they load
 */

const NostoRecommendations = {
  debug: false,

  /**
   * Initialize recommendations for a section
   * Called from Liquid: NostoRecommendations.init({...})
   */
  init: async function(config) {
    const {
      placementId,
      productId,
      sectionId,
      maxProducts = 10,
    } = config;

    try {
      const container = this.getContainer(sectionId);
      if (!container) {
        if (this.debug) console.warn(`[NostoRec] No container found for section: ${sectionId}`);
        return;
      }

      const nostoData = await this.fetchNostoRecommendations(placementId, productId);
      if (!nostoData || !nostoData.products || nostoData.products.length === 0) {
        this.hideSection(sectionId);
        return;
      }

      const limitedProducts = nostoData.products.slice(0, maxProducts);
      const nostoParams = nostoData.params;

      // Step 1: Immediately render placeholders with Nosto data
      this.renderPlaceholders(container, limitedProducts, nostoParams);
      this.updateSectionTitle(sectionId, nostoData.title);
      this.showSection(sectionId);

      // Step 2: Fetch full product cards and replace placeholders as they load
      const handles = limitedProducts.map(p => this.extractHandle(p.url));
      this.fetchAndReplacePlaceholders(container, handles, nostoParams);

      if (this.debug) console.log(`[NostoRec] Rendered ${limitedProducts.length} placeholders`);

    } catch (error) {
      console.error('[NostoRec] Error:', error);
      this.hideSection(sectionId);
    }
  },

  /**
   * Get the scroll-carousel or product-list container for a section
   */
  getContainer: function(sectionId) {
    const section = document.getElementById(`shopify-section-${sectionId}`);
    if (!section) return null;

    return section.querySelector('scroll-carousel') ||
           section.querySelector('product-list') ||
           section.querySelector('.predictive-search__products');
  },

  /**
   * Fetch recommendations from Nosto API
   */
  fetchNostoRecommendations: function(placementId, productId) {
    return new Promise((resolve) => {
      if (typeof nostojs === 'undefined') {
        if (this.debug) console.error('[NostoRec] nostojs is not loaded');
        resolve(null);
        return;
      }

      nostojs((api) => {
        const session = api.defaultSession();

        // Determine page type and set appropriate view
        let request;
        if (this.isProductPage()) {
          request = session.viewProduct(productId);
        } else if (this.isCategoryPage()) {
          request = session.viewCategory(this.getCurrentCollectionId());
        } else if (this.isSearchPage()) {
          request = session.viewSearch(this.getSearchTerm());
        } else if (this.isCartPage()) {
          request = session.viewCart();
        } else {
          request = session.viewFrontPage();
        }

        request
          .setPlacements([placementId])
          .load()
          .then((response) => {
            const placement = response?.recommendations?.[placementId];
            resolve({
              products: placement?.products || [],
              title: placement?.title || '',
              params: placement?.params || {}
            });
          })
          .catch((error) => {
            console.error('[NostoRec] Nosto API error:', error);
            resolve(null);
          });
      });
    });
  },

  /**
   * Render placeholder cards immediately using Nosto data
   */
  renderPlaceholders: function(container, nostoProducts, nostoParams) {
    // Clear existing products
    container.querySelectorAll('[data-nosto-product]').forEach(el => el.remove());

    const fragment = document.createDocumentFragment();

    nostoProducts.forEach((product) => {
      const handle = this.extractHandle(product.url);
      const placeholder = this.createPlaceholderCard(product, handle, nostoParams);
      fragment.appendChild(placeholder);
    });

    container.appendChild(fragment);
    this.refreshCarousel(container);
  },

  /**
   * Create a placeholder card from Nosto product data
   */
  createPlaceholderCard: function(nostoProduct, handle, nostoParams) {
    if (this.debug) {
      console.log(`[NostoRec] Creating placeholder for ${handle}:`, {
        imageUrl: nostoProduct.imageUrl,
        thumbUrl: nostoProduct.thumbUrl,
        image_url: nostoProduct.image_url,
        alternateImageUrls: nostoProduct.alternateImageUrls,
        fullProduct: nostoProduct
      });
    }

    const card = document.createElement('product-card');
    card.setAttribute('data-nosto-product', 'true');
    card.setAttribute('data-nosto-placeholder', 'true');
    card.setAttribute('handle', handle);

    if (nostoParams?.nosto) {
      card.setAttribute('nosto-slot-id', nostoParams.nosto);
    }

    // Try multiple possible image URL fields from Nosto
    const imageUrl = nostoProduct.imageUrl || nostoProduct.thumbUrl || nostoProduct.image_url || '';

    card.innerHTML = `
      <a href="${nostoProduct.url || `/products/${handle}`}" class="product-card-placeholder">
        <div class="product-card__figure">
          <img src="${imageUrl}" alt="${nostoProduct.name || ''}" loading="lazy" class="product-card__image">
        </div>
        <div class="product-card__info">
          <p class="product-card__title">${nostoProduct.name || ''}</p>
        </div>
      </a>
    `;

    return card;
  },

  /**
   * Fetch full product cards and replace placeholders as they load
   */
  fetchAndReplacePlaceholders: function(container, handles, nostoParams) {
    let completedCount = 0;
    const totalCount = handles.length;

    handles.forEach((handle) => {
      this.fetchProductCard(handle).then((cardData) => {
        completedCount++;

        if (cardData) {
          const placeholder = container.querySelector(`[data-nosto-placeholder][handle="${handle}"]`);
          if (placeholder) {
            const { element, stats } = cardData;

            // Transfer attributes
            element.setAttribute('data-nosto-product', 'true');
            element.setAttribute('handle', handle);

            if (nostoParams?.nosto) {
              element.setAttribute('nosto-slot-id', nostoParams.nosto);
            }

            // Append stats if available
            if (stats) {
              element.innerHTML += stats.innerHTML;
            }

            // Replace placeholder with full card
            placeholder.replaceWith(element);
          }
        } else {
          console.warn(`[NostoRec] No product card returned for handle: ${handle}`);
        }

        // When all fetches are complete, reset carousel to start
        if (completedCount === totalCount) {
          if (this.debug) console.log(`[NostoRec] All ${totalCount} fetches complete, resetting carousel position`);
          this.resetCarouselPosition(container);
        }
      });
    });
  },

  /**
   * Reset carousel to first position after all products loaded
   */
  resetCarouselPosition: function(container) {
    if (container.tagName !== 'SCROLL-CAROUSEL') return;

    // Second reset - after all Shopify API calls complete
    container.scrollLeft = 0;
    container.scrollTo({ left: 0, behavior: 'instant' });
    if (this.debug) console.log(`[NostoRec] Carousel scroll position set to 0`);
  },

  /**
   * Fetch a single product card from Shopify custom endpoint
   */
  fetchProductCard: async function(handle) {
    try {
      const url = this.buildProductUrl(handle);

      if (this.debug) {
        console.log(`[NostoRec] Fetching product: ${url}`);
      }

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Extract the product card from the response
      const productCard = doc.querySelector('.product_card product-card') ||
                          doc.querySelector('product-card');

      if (!productCard) {
        console.warn(`[NostoRec] No product card found for handle: ${handle}`);
        return null;
      }

      return {
        handle,
        element: productCard.cloneNode(true),
        stats: doc.querySelector('.product_stats')?.cloneNode(true)
      };

    } catch (error) {
      console.error(`[NostoRec] Error fetching product ${handle}:`, error);
      return null;
    }
  },

  /**
   * Build the URL for fetching a product card
   */
  buildProductUrl: function(handle) {
    const defaultLang = custom.default_lang || 'en';
    const currentLocale = theme?.settings?.locale || '';

    let url = `/pages/nosto-regular-feed/${handle}`;

    if (currentLocale && currentLocale !== defaultLang) {
      url = `/${currentLocale}${url}`;
    }

    return url;
  },

  /**
   * Refresh the carousel after adding products
   */
  refreshCarousel: function(container) {
    // Reset scroll position to start (first reset - when placeholders render)
    if (container.tagName === 'SCROLL-CAROUSEL') {
      container.scrollLeft = 0;
      container.scrollTo({ left: 0, behavior: 'instant' });
      this.attachCarouselScrollListener(container);
    }

    // Dispatch resize to trigger carousel recalculation
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
  },

  /**
   * Attach scroll listener for prev/next button state management.
   * The scroll-carousel custom element only attaches scroll listeners if it
   * has children at construction time. Since Nosto injects products after
   * construction, the edge events never fire and the prev button stays
   * permanently disabled/hidden. This mirrors the theme's edge detection
   * logic and dispatches the same custom events the buttons already listen for.
   */
  attachCarouselScrollListener: function(container) {
    if (container._nostoScrollListener) return;
    container._nostoScrollListener = true;

    const edgeState = { nearingStart: true, nearingEnd: true, leavingStart: true, leavingEnd: true };
    let ticking = false;

    container.addEventListener('scroll', function() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function() {
        ticking = false;
        const threshold = 100;
        const scrollLeft = Math.round(Math.abs(container.scrollLeft));
        const maxScroll = container.scrollWidth - container.clientWidth;

        if (scrollLeft < threshold && edgeState.nearingStart) {
          container.dispatchEvent(new CustomEvent('scroll:edge-nearing', { detail: { position: 'start' } }));
          edgeState.nearingStart = false;
          edgeState.leavingStart = true;
        }
        if (scrollLeft >= threshold && edgeState.leavingStart) {
          container.dispatchEvent(new CustomEvent('scroll:edge-leaving', { detail: { position: 'start' } }));
          edgeState.leavingStart = false;
          edgeState.nearingStart = true;
        }
        if (maxScroll < scrollLeft + threshold && edgeState.nearingEnd) {
          container.dispatchEvent(new CustomEvent('scroll:edge-nearing', { detail: { position: 'end' } }));
          edgeState.nearingEnd = false;
          edgeState.leavingEnd = true;
        }
        if (maxScroll >= scrollLeft + threshold && edgeState.leavingEnd) {
          container.dispatchEvent(new CustomEvent('scroll:edge-leaving', { detail: { position: 'end' } }));
          edgeState.leavingEnd = false;
          edgeState.nearingEnd = true;
        }
      });
    });
  },

  /**
   * Extract handle from Nosto product URL
   */
  extractHandle: function(url) {
    if (!url) return null;
    const parts = url.split('/');
    return parts[parts.length - 1];
  },

  /**
   * Page type detection helpers
   */
  isProductPage: function() {
    return document.body.classList.contains('product') ||
           window.location.pathname.includes('/products/');
  },

  isCategoryPage: function() {
    return document.body.classList.contains('collection') ||
           window.location.pathname.includes('/collections/');
  },

  isSearchPage: function() {
    return window.location.pathname.includes('/search');
  },

  isCartPage: function() {
    return window.location.pathname.includes('/cart');
  },

  getCurrentCollectionId: function() {
    // Try to get from theme settings or meta tag
    const collectionId =
      (typeof theme !== 'undefined' && theme?.settings?.collection_id) ||
      document.querySelector('meta[name="collection-id"]')?.content;

    // Always return a string to avoid passing undefined to consumers
    return collectionId || '';
  },

  getSearchTerm: function() {
    const params = new URLSearchParams(window.location.search);
    return params.get('q') || '';
  },

  /**
   * Update section title from Nosto response
   */
  updateSectionTitle: function(sectionId, title) {
    if (!title) return;

    const section = document.getElementById(`shopify-section-${sectionId}`);
    if (!section) return;

    const titleElement = section.querySelector('.mo-header-heading');
    if (titleElement) {
      titleElement.textContent = title;
      titleElement.style.display = '';
    }
  },

  /**
   * Show/hide section
   */
  showSection: function(sectionId) {
    const section = document.getElementById(`shopify-section-${sectionId}`);
    if (section) {
      section.style.display = '';
    }
  },

  hideSection: function(sectionId) {
    const section = document.getElementById(`shopify-section-${sectionId}`);
    if (section) {
      section.style.display = 'none';
    }
  },

  /**
   * Enable debug mode
   */
  enableDebug: function() {
    this.debug = true;
    console.log('[NostoRec] Debug mode enabled');
  }
};

// Export for use in Liquid
window.NostoRecommendations = NostoRecommendations;
